import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Search, Bell, Plus, Zap, Settings, CreditCard, LogOut, Trash2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useStore } from '../store/useStore';
import { useAuthStore } from '../store/useAuthStore';
import { ROUTES } from '../routes';

export function Header() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const openModal = useStore((state) => state.openModal);
  const inboxItems = useStore((state) => state.inboxItems);
  const { user, signOut } = useAuthStore();

  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setIsProfileMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getInitials = () => {
    if (!user) return 'U';
    const meta = user.user_metadata;
    if (meta?.first_name && meta?.last_name) {
      return `${meta.first_name[0]}${meta.last_name[0]}`.toUpperCase();
    }
    if (meta?.first_name) {
      return meta.first_name[0].toUpperCase();
    }
    if (meta?.pseudo) {
      return meta.pseudo[0].toUpperCase();
    }
    return user.email ? user.email[0].toUpperCase() : 'U';
  };

  const handleLocaleChange = async (locale: 'fr' | 'en') => {
    void i18n.changeLanguage(locale);
    localStorage.setItem('vb_locale', locale);
    if (user) {
      await supabase.from('profiles').update({ locale }).eq('id', user.id);
    }
  };

  const activeLang = i18n.language.startsWith('en') ? 'en' : 'fr';

  return (
    <header className="h-16 border-b border-zinc-700 bg-zinc-950 backdrop-blur-md sticky top-0 z-10 flex items-center justify-between px-8">
      <div className="flex items-center gap-4 flex-1">
        <div className="relative w-full max-w-md hidden sm:block">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            type="text"
            placeholder={t('header.search_placeholder')}
            className="w-full bg-zinc-900 border border-zinc-700 rounded-full pl-10 pr-4 py-1.5 text-sm text-zinc-200 placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-violet-500/40 transition-shadow"
          />
        </div>
      </div>

      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(ROUTES.synapse)}
          className="relative px-3 py-1.5 text-zinc-400 hover:text-zinc-100 transition-colors rounded-full hover:bg-zinc-800/50"
          title={t('header.synapse')}
        >
          <Zap className="w-5 h-5" />
          {inboxItems.length > 0 && (
            <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-blue-500 rounded-full border-2 border-zinc-950"></span>
          )}
        </button>
        <button
          onClick={() => openModal('create')}
          data-onboarding="header-new"
          className="flex items-center gap-2 bg-zinc-100 hover:bg-zinc-200 text-zinc-950 px-4 py-1.5 rounded-full text-sm font-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">{t('header.new')}</span>
        </button>
        <button className="relative p-2 text-zinc-400 hover:text-zinc-100 transition-colors rounded-full hover:bg-zinc-800/50">
          <Bell className="w-5 h-5" />
          <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-blue-500 rounded-full border-2 border-zinc-950"></span>
        </button>
        <div className="relative" ref={profileMenuRef}>
          <button
            onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
            className="w-8 h-8 rounded-full bg-zinc-800 border border-zinc-600 overflow-hidden flex items-center justify-center hover:ring-2 hover:ring-violet-500/40 transition-all cursor-pointer text-xs font-semibold text-zinc-300"
            title={t('header.profile_menu')}
          >
            {getInitials()}
          </button>
          {isProfileMenuOpen && (
            <div className="absolute right-0 mt-2 w-56 bg-zinc-900 border border-zinc-700 rounded-xl shadow-xl overflow-hidden z-50 py-1">
              <div className="px-3 py-2 border-b border-zinc-800/50 mb-1">
                <p className="text-sm font-medium text-zinc-200 truncate">
                  {user?.user_metadata?.first_name ||
                    user?.user_metadata?.pseudo ||
                    t('header.user_fallback')}
                </p>
                <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
              </div>
              <button
                onClick={() => {
                  setIsProfileMenuOpen(false);
                  navigate(ROUTES.parametres);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-800 rounded-md transition-colors text-left"
              >
                <Settings className="w-4 h-4" />
                {t('header.settings')}
              </button>
              <button
                onClick={() => {
                  setIsProfileMenuOpen(false);
                  navigate(ROUTES.subscription);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-800 rounded-md transition-colors text-left"
              >
                <CreditCard className="w-4 h-4" />
                {t('header.subscription')}
              </button>
              {import.meta.env.DEV && (
                <div className="border-t border-zinc-700/50 mt-1 pt-1">
                  <button
                    onClick={async () => {
                      if (!user) return;
                      const confirmed = window.confirm(t('header.dev_purge_confirm'));
                      if (!confirmed) return;
                      setIsProfileMenuOpen(false);
                      const uid = user.id;
                      await Promise.all([
                        supabase.from('tasks').delete().eq('user_id', uid),
                        supabase.from('goals').delete().eq('user_id', uid),
                        supabase.from('notes').delete().eq('user_id', uid),
                        supabase.from('events').delete().eq('user_id', uid),
                        supabase.from('contacts').delete().eq('user_id', uid),
                        supabase.from('habits').delete().eq('user_id', uid),
                        supabase
                          .from('inbox_items')
                          .delete()
                          .eq('user_id', uid)
                          .neq('item_type', 'audio'),
                      ]);
                      const activeBrainId = useStore.getState().activeBrainId;
                      if (activeBrainId) {
                        await supabase
                          .from('visual_brain_canvases')
                          .update({ nodes: [], edges: [] })
                          .eq('id', activeBrainId)
                          .eq('user_id', uid);
                      }
                      const audioItems = useStore
                        .getState()
                        .inboxItems.filter((i) => i.itemType === 'audio');
                      useStore.setState({
                        tasks: [],
                        goals: [],
                        notes: [],
                        events: [],
                        contacts: [],
                        habits: [],
                        inboxItems: audioItems,
                        nodes: [],
                        edges: [],
                      });
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-amber-400 hover:bg-zinc-800 rounded-md transition-colors text-left"
                  >
                    <Trash2 className="w-4 h-4" />
                    {t('header.dev_purge')}
                  </button>
                </div>
              )}
              <div className="border-t border-zinc-700/50 mt-1 pt-1">
                <div className="px-3 py-1.5 flex items-center gap-1.5">
                  <button
                    onClick={() => handleLocaleChange('fr')}
                    className={`px-2.5 py-0.5 rounded-full text-xs font-semibold transition-colors ${
                      activeLang === 'fr'
                        ? 'bg-violet-600 text-white'
                        : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800'
                    }`}
                  >
                    FR
                  </button>
                  <button
                    onClick={() => handleLocaleChange('en')}
                    className={`px-2.5 py-0.5 rounded-full text-xs font-semibold transition-colors ${
                      activeLang === 'en'
                        ? 'bg-violet-600 text-white'
                        : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800'
                    }`}
                  >
                    EN
                  </button>
                </div>
              </div>
              <div className="border-t border-zinc-700/50 mt-1 pt-1">
                <button
                  onClick={async () => {
                    setIsProfileMenuOpen(false);
                    await signOut();
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-zinc-800 rounded-md transition-colors text-left"
                >
                  <LogOut className="w-4 h-4" />
                  {t('header.logout')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
